/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles
} from '@material-ui/core';
import { useRoles, ReadAccessRole, CreateLookupsRole } from 'src/components/AccessControl';
import PersonIcon from '@material-ui/icons/Person';
import DescriptionIcon from '@material-ui/icons/Description';
import BusinessIcon from '@material-ui/icons/Business';
import PublishIcon from '@material-ui/icons/Publish';
import CalendarIcon from '@material-ui/icons/CalendarTodayTwoTone';
import SearchIcon from '@material-ui/icons/Search';
import AppsIcon from '@material-ui/icons/Apps';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';

let environment = '';

const navConfig = [
  {
    subheader: 'Navigation',
    items: [
      {
        title: 'Overview',
        icon: AppsIcon,
        href: '/home',
      },
      {
        title: 'Clients',
        icon: PersonIcon,
        href: '/clients',
        accessControlRole: ReadAccessRole,
      },
      {
        title: 'Contracts',
        icon: DescriptionIcon,
        href: '/contracts',
        accessControlRole: ReadAccessRole,
      },
      {
        title: 'Properties',
        icon: BusinessIcon,
        href: '/properties',
        accessControlRole: ReadAccessRole,
      }
    ]
  },
  {
    subheader: 'Utilities',
    items: [
      {
        title: 'Feeds',
        href: '/feeds',
        icon: PublishIcon,
        accessControlRole: ReadAccessRole,
      },
      {
        title: 'Monthly Close',
        href: '/monthly-close',
        icon: CalendarIcon,
        accessControlRole: ReadAccessRole,
      },
      {
        title: 'Edit Lookups',
        href: '/lookups',
        icon: SearchIcon,
        accessControlRole: CreateLookupsRole,
      }
    ]
  }
];

function renderNavItems({ items, roles, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({
          acc,
          item,
          roles,
          ...rest
        }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  roles,
  depth = 0
}) {
  const key = item.title + depth;

  if (item.title === 'Edit Lookups' && (item.accessControlRole && !roles.includes(item.accessControlRole))) {
    return acc;
  }

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
        disabled={item.accessControlRole && !roles.includes(item.accessControlRole)}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          ...roles,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
        disabled={item.accessControlRole && roles && !roles.includes(item.accessControlRole)}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 105px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

function NavBar({ openMobile, onMobileClose, }) {
  const classes = useStyles();
  const location = useLocation();
  const userRoles = useRoles();
  const { user } = useSelector((state) => state.account);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  if (process.env.NODE_ENV === 'development') {
    environment = 'Dev Testing Environment';
  } else if (process.env.NODE_ENV === 'test') {
    environment = 'QA Testing Environment';
  }

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={2}
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/app/account">
              <Avatar
                alt="User"
                className={classes.avatar}
              />
            </RouterLink>
          </Box>
          <Box
            mt={2}
            textAlign="center"
          >
            <Link
              component={RouterLink}
              to="/"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {`${user?.name}`}
            </Link>
          </Box>
        </Box>
        <Box
          textAlign="center"
        >
          <Link>
            {
              `${environment}`
            }
          </Link>
        </Box>
        <Divider />
        <Box p={2}>
          {navConfig.map((config) => (
            <List
              key={config.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {config.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname,
                roles: userRoles,
              })}
            </List>
          ))}
        </Box>
        <Divider />
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
